<template>
    <div>
        <popup-modal position="center" :title="title" @close="$emit('close')" icon="forum">
            <div class="w-600">
                <loading v-if="fetchingScores" class="pt-48 mb-48"></loading>

                <template v-else>
                    <div class="flex items-center justify-between">
                        <p v-html="competenceTemplateTitle"></p>
                        <simple-button
                            icon="edit"
                            small
                            @click="edit"
                        >
                            {{$t('competence-templates.change')}}
                        </simple-button>
                    </div>

                    <template v-if="!noTemplateLinked">
                        <div class="mt-24">
                            <template v-if="competenceCount">
                                <div
                                    class="border-b-2 border-primary-20"
                                    v-for="(competence, index) in competences"
                                    :key="'competence-' + competence.pivot.id"
                                >
                                    <competence
                                        class="mb-32"
                                        :class="[ index > 0 ? 'pt-32' : 'pt-16' ]"
                                        :competence="competence"
                                        :application="application"
                                    />
                                </div>
                            </template>
                            <div v-else>
                                <p>{{$t('competence-templates.no_competences') }}</p>
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </popup-modal>

        <popup-modal-confirm
            v-if="showTemplateConfirmationPopup"
            :title="$t('competence-templates.change')"
            :confirmText="$t('general.update')"
            :message="$t('competence-templates.warning')"
            :working="fetchingScores"
            :showExclamation="true"
            @close="cancelTemplateChange"
            @confirm="confirmTemplateChange();"
        >
        </popup-modal-confirm>

    </div>
</template>

<script>
import {isObject} from "lodash";
import {normalizeJson} from '../../shared/helpers';
import * as jobService from "../services/JobService";
import * as competences from '../services/CompetenceService';
import * as competenceTemplateService from "../services/CompetenceTemplateService";
import Application from '../models/Application';
import ApplicationCompetence from '../models/ApplicationCompetence';
import CompetenceTemplate from "../models/CompetenceTemplate";

import PopupModal from './ui/PopupModal.vue';
import SimpleButton from './ui/SimpleButton.vue';
import Loading from './ui/Loading.vue';
import CompetenceScore from './Competence.vue';
import CompetenceEditor from './ui/CompetenceEditor.vue';
import CompetenceImporter from './ui/CompetenceImporter.vue';
import {mapState, mapActions} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import NewCompetenceTemplate from "./applications/NewModuleTemplate.vue";
import ModuleTemplateEdit from "./ModuleTemplateEdit";
import CompetenceTemplates from "./ui/CompetenceTemplates";
import SvgIcon from "./ui/SvgIcon.vue";
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";

export default {
    components: {
        'popup-modal': PopupModal,
        'simple-button': SimpleButton,
        'loading': Loading,
        'competence': CompetenceScore,
        'competence-editor': CompetenceEditor,
        'competence-importer': CompetenceImporter,
        NewCompetenceTemplate,
        ModuleTemplateEdit,
        CompetenceTemplates,
        PopupModalConfirm,
        SvgIcon
    },
    props: {
        application: {
            required: true,
            type: Application,
        },
        canManage: Boolean
    },
    data: function () {
        return {
            fetchingScores: true,
            showTemplateConfirmationPopup: false,
            addingCompetence: false,
            importingCompetences: false,
            noTemplateLinked: false,
            template: null,
            competenceTemplateId: this.application.stage.job.competence_template_id
        }
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        can() {
            return {
                competences: this.canManage,
                jobs: {update: this.$can(['jobs.manage', 'jobs.manage.' + this.application.stage.job_id])},
            };
        },
        title() {
            return '<span class="font-normal">' + this.$t('applications.competence_scores') + '</span> ' + this.application.fullName;
        },
        job() {
            return this.application.stage.job;
        },
        competences() {
            return this.competenceTemplate.competences.sort((a, b) => a.pivot.order - b.pivot.order)
        },
        competenceCount() {
            return this.competences.length || 0;
        },
        competenceTemplate() {
            return useRepo(CompetenceTemplate)
                .with('competences')
                .whereId(this.competenceTemplateId)
                .first();
        },
        competenceTemplateTitle() {
            if (this.competenceTemplate) {
                return `${this.$t('settings.competence-templates.title')}: <span class="font-semibold text-primary">${this.competenceTemplate.titleLocale}</span>`;  //this.$t('settings.competence-templates.title') +  this.competenceTemplate.title || '';
            }

            return this.$t('competence-templates.no_competence_template_found');
        },
    },
    methods: {
        ...mapActions(useMainStore, ['setTemplateModuleInfo']),
        setModuleInfo() {
            this.setTemplateModuleInfo({
                templateModel: 'competence-templates',
                model: 'competences',
                type: 'competence',
                children: 'competences',
                autocomplete: true,
                hasPivot: true,
                title: {
                    attribute: 'title',
                    locale: this.locale
                }
            });
        },
        service() {
            return competenceTemplateService;
        },
        edit() {
            this.$router.push({
                name: 'settings.competence-templates',
            });
        },
        toggleConfirmTemplate(show = true) {
            this.showTemplateConfirmationPopup = show;
        },
        cancelTemplateChange() {
            this.competenceTemplateId = this.application.stage.job.competence_template_id;
            this.toggleConfirmTemplate(false);
        },
        confirmTemplateChange() {
            this.toggleConfirmTemplate(false);

            jobService.update(this.job.id, {
                data: {
                    attributes: {
                        competence_template_id: this.competenceTemplateId,
                    }
                },
            }).then(() => {
                this.fetchCompetences();
            });
        },
        fetchCompetences() {
            if (!this.competenceTemplateId) {
                this.noTemplateLinked = true;
                this.fetchingScores = false;
                return;
            }

            this.fetchingScores = true;
            let hasData;
            Promise.all([
                competenceTemplateService.show(this.competenceTemplateId).then(result => {
                    hasData = isObject(result.data);
                    if (!hasData) {
                        return false;
                    }

                    useRepo(CompetenceTemplate).save(normalizeJson(result.data));
                }).then(() => {
                    if (!hasData) {
                        return false;
                    }

                    competences.indexForApplication(this.application.id)
                        .then(result => {
                            const data = normalizeJson(result).map((d) => {
                                d.score = parseInt(d.score, 10) || 0;
                                return d;
                            });
                            return useRepo(ApplicationCompetence).save(data);
                        });
                })


            ]).then(() => {
                this.noTemplateLinked = !hasData
                this.fetchingScores = false;
            });
        }
    },
    mounted () {
        this.setModuleInfo();
        this.fetchCompetences();
    }
}
</script>
